import { dist2, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, SendaNpc } from '../types.ts';
import { enemiesNear, targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { castSkill } from '../battle/skills.ts';
import { litterOrder } from '../battle/litter.ts';
import { naveTrace } from '../battle/ermita-layout.ts';
import { ERM_LAMP, ERM_MIRROR, ERM_SLOT, ERM_SPLIT, ERM_STONE, ermLampReady, ermNoHeal } from '../battle/ermita.ts';

/**
 * The cleric's chapter 4 played by the lab's bot (docs/sendas/clerigo.md, «La Ermita»; ai/senda-bot-cleric.ts hands it
 * the fight): one that knows the act's rule and one that doesn't.
 *
 * - The memory of Aud's vigil: who knows hushes the Doliente's wail with its holy blows (it walks into her veil to do it),
 *   cuts the tongues at a lamp first, lights each lamp on its minute and lights again the ones put out behind her, the
 *   altar's above all; who doesn't lights the lamps in order as they come, fights the nearest of the dead and leaves the
 *   tongues and the wail be.
 * - The nave of the mirrors (the test): both set Aud's mirror in its place and heal Tessa. Who knows peels the limpets off
 *   Tessa and off the mirrors of the way first, and works the nave out (the way that splits once, to both); who doesn't
 *   sets the obvious way to the altar and fights what comes, the door left dark.
 *
 * It only moves the hero, picks its target and casts, as a player's commands would.
 */

const THINK = 36;

const go = (s: BattleState, h: Hero, x: number, y: number, slack = 300): void => {
  if (!h.moving && withinRange(h.x, h.y, x, y, 250)) return;
  if (!h.moving || Math.abs(h.destX - x) + Math.abs(h.destY - y) > slack) orderMove(s, h, x, y);
};

const nearestTo = (list: Enemy[], x: number, y: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

const strike = (h: Hero, t: Enemy): void => {
  h.target = t.id;
  h.forced = true;
};

const person = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);

/** Its own heals: a mend on itself (not in a wail: it wouldn't work), the sanctuary on Tessa, dawn when low. */
const heals = (s: BattleState, h: Hero): void => {
  const r = s.rules?.senda?.litter;
  const t = person(s, 'tessa');
  const meLow = h.hp * 10 < h.stats.maxHp * 6;
  if (ermNoHeal(s)) return;
  const beside = !!r && !!t && withinRange(h.x, h.y, t.x, t.y, r.mendReach - 300);
  if (h.skillCd[0] === 0 && (meLow || (beside && t && t.hp <= t.maxHp - 4))) castSkill(s, h, { t: 'skill', slot: 0, id: h.id });
  if (h.skillCd[1] === 0 && t && beside && (t.hp * 2 <= t.maxHp || h.hp * 2 < h.stats.maxHp)) castSkill(s, h, { t: 'skill', slot: 1, x: t.x, y: t.y });
  if (h.skillCd[2] === 0 && h.hp * 10 < h.stats.maxHp * 3) castSkill(s, h, { t: 'skill', slot: 2 });
};

const BUSY = new WeakMap<BattleState, { wait: number; busy: boolean }>();

/** Between one piece of work and the next, a moment to look (as a player would). */
const ready = (s: BattleState): boolean => {
  const w = s.senda?.litter;
  let m = BUSY.get(s);
  if (!m) {
    m = { wait: s.tick + THINK, busy: false };
    BUSY.set(s, m);
  }
  if (w && w.job >= 0) {
    m.busy = true;
    return false;
  }
  if (m.busy) {
    m.busy = false;
    m.wait = s.tick + THINK;
  }
  return s.tick >= m.wait;
};

/* ---------- the memory: Aud's last vigil ---------- */

const vigilBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  const V = r?.hermit?.vigil;
  const m = w?.erm;
  if (!r?.hermit || !w || !V || !m) return;
  const H = r.hermit;
  heals(s, h);
  const boss = m.boss ? s.enemies.find((e) => e.id === m.boss && e.hp > 0) : undefined;
  if (knows) {
    // Her wail: hushed with holy blows (close enough to strike her).
    if (boss && ermNoHeal(s) && h.hp * 10 > h.stats.maxHp * 4) {
      w.job = -1;
      strike(h, boss);
      if (!withinRange(h.x, h.y, boss.x, boss.y, h.stats.range - 200)) go(s, h, boss.x, boss.y + 400, 300);
      return;
    }
    // A tongue: cut it (those at a lamp, and those going for the altar's, first).
    const last = V.lamps.length - 1;
    const tg = m.tongues
      .map((t) => ({ t, e: s.enemies.find((e) => e.id === t.id && e.hp > 0) }))
      .filter((x): x is { t: (typeof m.tongues)[number]; e: Enemy } => !!x.e)
      // (Not at her feet, inside her veil: once it is out on the floor.)
      .filter((x) => x.t.at >= 0 || !boss || !withinRange(x.e.x, x.e.y, boss.x, boss.y, V.veil.r + 500))
      .sort((a, b) => (b.t.at >= 0 ? 10 : 0) + b.t.lamp - ((a.t.at >= 0 ? 10 : 0) + a.t.lamp))[0];
    if (tg && (tg.t.at >= 0 || tg.t.lamp === last || withinRange(h.x, h.y, tg.e.x, tg.e.y, 4000))) {
      w.job = -1;
      strike(h, tg.e);
      if (!withinRange(h.x, h.y, tg.e.x, tg.e.y, h.stats.range - 200)) go(s, h, tg.e.x, tg.e.y, 300);
      return;
    }
    // The last minute: by the altar's lamp.
    if (m.lit[last] && w.job < 0) {
      const l = V.lamps[last] as { x: number; y: number };
      const foes = enemiesNear(s, l.x, l.y, 3000, (e) => targetable(s, e) && e.id !== m.boss);
      const t = nearestTo(foes, h.x, h.y);
      if (t) strike(h, t);
      else if (!withinRange(h.x, h.y, l.x, l.y + 1200, 700)) go(s, h, l.x, l.y + 1200, 400);
      return;
    }
  }
  if (w.job >= 0) return;
  // The lamps: the altar's when it is ready; then those put out behind her; then the next on its minute.
  const last = V.lamps.length - 1;
  let want = -1;
  if (!m.lit[last] && ermLampReady(H, last, s.waveT)) want = last;
  if (want < 0)
    for (let i = 0; i < last; i++) {
      if (m.lit[i] || !ermLampReady(H, i, s.waveT)) continue;
      // (Who doesn't know lights them once, in order, and never goes back for one put out.)
      if (!knows && i <= m.front) continue;
      want = i;
      break;
    }
  if (want >= 0 && ready(s)) {
    litterOrder(s, h, ERM_LAMP + want);
    return;
  }
  // The rest: a tongue on its way (who knows), else the dead nearest.
  const foes = enemiesNear(s, h.x, h.y, 9000, (e) => targetable(s, e) && e.id !== m.boss && (knows || !m.tongues.some((t) => t.id === e.id)));
  const t = nearestTo(foes, h.x, h.y);
  if (t) {
    strike(h, t);
    if (!withinRange(h.x, h.y, t.x, t.y, h.stats.range)) go(s, h, t.x, t.y, 600);
    return;
  }
  h.target = 0;
  // Waiting by the next lamp to come.
  const next = m.lit.findIndex((v) => !v);
  const l = V.lamps[next >= 0 ? next : last] as { x: number; y: number };
  go(s, h, l.x + (l.x > 0 ? -600 : 600), l.y + 300, 500);
};

/* ---------- the nave of the mirrors (the test) ---------- */

const naveBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  const N = r?.hermit?.nave;
  const m = w?.erm;
  const t = person(s, 'tessa');
  if (!r?.hermit || !w || !N || !m || !t) return;
  const H = r.hermit;
  heals(s, h);
  // Piet down: up (both know).
  const p = person(s, 'piet');
  if (p && p.hp <= 0 && w.job < 0) {
    if (!withinRange(h.x, h.y, p.x, p.y, r.liftReach - 300)) go(s, h, p.x + 300, p.y + 200, 200);
    else if (h.moving) orderMove(s, h, h.x, h.y);
    return;
  }
  // Limpets: on the hero, killed; on Tessa, peeled (who knows); on a mirror of the way, peeled (who knows).
  const mine = w.limpets.filter((l) => l.on === 2).map((l) => s.enemies.find((e) => e.id === l.id && e.hp > 0)).find((e): e is Enemy => !!e);
  if (mine) {
    strike(h, mine);
    return;
  }
  if (knows) {
    const on = w.limpets.filter((l) => l.on === 1).map((l) => s.enemies.find((e) => e.id === l.id && e.hp > 0)).find((e): e is Enemy => !!e);
    const held = m.onMirror.find((x) => (m.covered >> x.m) & 1);
    const cover = held ? s.enemies.find((e) => e.id === held.id && e.hp > 0) : undefined;
    const l = on ?? cover;
    if (l && w.job < 0) {
      strike(h, l);
      if (!withinRange(h.x, h.y, l.x, l.y, h.stats.range - 200)) go(s, h, l.x, l.y, 300);
      return;
    }
  }
  // Tessa at half: to her, to mend her (a heal on itself beside her reaches her).
  if (knows && t.hp * 2 <= t.maxHp && h.skillCd[0] === 0 && w.job < 0 && !withinRange(h.x, h.y, t.x, t.y, r.mendReach - 400)) {
    go(s, h, t.x, t.y + 600, 300);
    return;
  }
  // The dead at Tessa: fought.
  const atHer = enemiesNear(s, t.x, t.y, 1500, (e) => targetable(s, e) && !m.onMirror.some((x) => x.id === e.id));
  if (atHer.length && w.job < 0) {
    const e = nearestTo(atHer, t.x, t.y) as Enemy;
    strike(h, e);
    if (!withinRange(h.x, h.y, e.x, e.y, h.stats.range)) go(s, h, e.x, e.y, 400);
    return;
  }
  if (m.both >= 0) {
    // Lit: by Tessa, keeping it.
    h.target = 0;
    if (!withinRange(h.x, h.y, t.x, t.y, 1600)) go(s, h, t.x, t.y + 700, 400);
    return;
  }
  if (!ready(s)) return;
  h.target = 0;
  // Aud's mirror first.
  if (m.aud === 0) return litterOrder(s, h, ERM_STONE);
  if (m.aud === 1) return litterOrder(s, h, ERM_SLOT);
  // Who knows: the answer, piece by piece along the beam. Who doesn't: the obvious way.
  const want = knows ? N.answer.turns : obvious(s);
  const B = { lantern: H.lantern, altar: H.altar, door: H.door, mirrors: H.mirrors, splits: H.splits };
  const b = naveTrace(B, w.turns, m.splits, 0, m.covered);
  for (let i = 0; i < 6; i++)
    if ((b.touched >> i) & 1 && want && w.turns[i] !== want[i]) {
      litterOrder(s, h, ERM_MIRROR + i);
      return;
    }
  if (knows)
    for (let i = 0; i < 2; i++)
      if ((b.touched >> (6 + i)) & 1 && m.splits[i] !== N.answer.splits[i]) {
        litterOrder(s, h, ERM_SPLIT + i);
        return;
      }
  if (!withinRange(h.x, h.y, t.x, t.y, 1800)) go(s, h, t.x, t.y + 700, 400);
};

/** The obvious way: the first mirror the lantern meets, and the next, turned to the altar (whatever the rest). */
const OBV = new WeakMap<BattleState, number[] | null>();
const obvious = (s: BattleState): number[] | null => {
  if (OBV.has(s)) return OBV.get(s) ?? null;
  const H = s.rules?.senda?.litter?.hermit;
  const w = s.senda?.litter;
  if (!H || !w) return null;
  const B = { lantern: H.lantern, altar: H.altar, door: H.door, mirrors: H.mirrors, splits: H.splits };
  // Of the ways of turning the mirrors, the one with fewest pieces touched that lights the altar.
  let best: number[] | null = null;
  let bn = 99;
  const t = [0, 0, 0, 0, 0, 0];
  for (let c = 0; c < 729; c++) {
    let x = c;
    for (let i = 0; i < 6; i++) {
      t[i] = x % 3;
      x = (x - (x % 3)) / 3;
    }
    const b = naveTrace(B, t, w.erm?.splits ?? [0, 0]);
    if (!b.altar || b.door) continue;
    let n = 0;
    for (let v = b.touched; v; v &= v - 1) n++;
    if (n < bn) {
      bn = n;
      best = [...t];
    }
  }
  OBV.set(s, best);
  return best;
};

/** The cleric's chapter 4 (true: it was one of its fights). */
export const ermitaBot = (s: BattleState, h: Hero, knows: boolean): boolean => {
  const r = s.rules?.senda?.litter;
  if (r?.kind === 'vigil') vigilBot(s, h, knows);
  else if (r?.kind === 'nave') naveBot(s, h, knows);
  else return false;
  return true;
};
